import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ConversionProcess = () => {
  const [activePhase, setActivePhase] = useState(0);

  const phases = [
    {
      id: 1,
      title: "Network Assessment",
      duration: "1-2 weeks",
      icon: "Search",
      description: "Comprehensive evaluation of your current infrastructure, identifying bottlenecks and optimization opportunities.",
      details: [
        "Current network performance analysis",
        "Infrastructure mapping and documentation", 
        "Bandwidth requirement assessment",
        "Future scalability planning",
        "Risk assessment and mitigation planning"
      ],
      businessImpact: "Zero disruption to daily operations",
      color: "blue"
    },
    {
      id: 2,
      title: "Custom Design",
      duration: "1-2 weeks",
      icon: "PenTool",
      description: "Tailored fiber optic solution designed specifically for your business needs and building infrastructure.",
      details: [
        "Custom network topology design",
        "Equipment specification and sourcing",
        "Installation pathway planning",
        "Compliance and permit coordination",
        "Timeline and resource allocation"
      ],
      businessImpact: "Detailed project roadmap with clear milestones",
      color: "purple"
    },
    {
      id: 3,
      title: "Phased Installation",
      duration: "2-6 weeks",
      icon: "Settings",
      description: "Strategic installation approach that minimizes business disruption while ensuring quality implementation.",
      details: [
        "Staged installation to maintain operations",
        "Professional cable management",
        "Equipment installation and configuration",
        "Network integration and testing",
        "Staff training and documentation"
      ],
      businessImpact: "Minimal operational disruption with parallel systems",
      color: "orange"
    },
    {
      id: 4,
      title: "Testing & Validation",
      duration: "1 week",
      icon: "CheckCircle",
      description: "Comprehensive testing to ensure optimal performance and reliability before full deployment.",
      details: [
        "End-to-end connectivity testing",
        "Performance benchmarking",
        "Security validation",
        "Failover and redundancy testing",
        "User acceptance testing"
      ],
      businessImpact: "Guaranteed performance before go-live",
      color: "green"
    },
    {
      id: 5,
      title: "Ongoing Support",
      duration: "Lifetime",
      icon: "Headphones",
      description: "Continuous monitoring and support to ensure your fiber network delivers peak performance.",
      details: [
        "24/7 network monitoring",
        "Proactive maintenance scheduling",
        "Emergency response service",
        "Performance optimization",
        "Technology upgrade planning"
      ],
      businessImpact: "Peace of mind with expert support",
      color: "teal"
    }
  ];

  const getColorClasses = (color, isActive) => {
    const colors = {
      blue: isActive ? 'bg-blue-500 text-white' : 'bg-blue-100 text-blue-700',
      purple: isActive ? 'bg-purple-500 text-white' : 'bg-purple-100 text-purple-700',
      orange: isActive ? 'bg-orange-500 text-white' : 'bg-orange-100 text-orange-700',
      green: isActive ? 'bg-green-500 text-white' : 'bg-green-100 text-green-700',
      teal: isActive ? 'bg-teal-500 text-white' : 'bg-teal-100 text-teal-700'
    };
    return colors?.[color] || colors?.blue;
  };

  return (
    <div className="bg-surface rounded-xl p-8 mb-12">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-text-primary mb-4">
          Our Proven 5-Phase Process
        </h2>
        <p className="text-text-secondary text-lg max-w-3xl mx-auto">
          Every conversion follows our systematic approach, ensuring minimal disruption 
          and maximum performance for your business transformation.
        </p>
      </div>
      {/* Phase Navigation */}
      <div className="flex flex-wrap justify-center gap-4 mb-8">
        {phases?.map((phase, index) => (
          <button
            key={phase?.id}
            onClick={() => setActivePhase(index)}
            className={`flex items-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
              activePhase === index 
                ? getColorClasses(phase?.color, true)
                : getColorClasses(phase?.color, false)
            }`}
          >
            <Icon name={phase?.icon} size={18} />
            <span className="hidden sm:inline">Phase {phase?.id}</span>
            <span className="sm:hidden">{phase?.id}</span>
          </button>
        ))}
      </div>
      {/* Active Phase Details */}
      <div className="bg-white rounded-lg p-8 shadow-brand-moderate">
        <div className="grid lg:grid-cols-2 gap-8">
          <div>
            <div className="flex items-center mb-4">
              <div className={`p-3 rounded-lg mr-4 ${getColorClasses(phases?.[activePhase]?.color, true)}`}>
                <Icon name={phases?.[activePhase]?.icon} size={24} />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-text-primary">
                  {phases?.[activePhase]?.title}
                </h3>
                <p className="text-text-secondary">
                  Duration: {phases?.[activePhase]?.duration}
                </p>
              </div>
            </div>
            
            <p className="text-text-secondary mb-6 text-lg">
              {phases?.[activePhase]?.description}
            </p>

            <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 mb-6">
              <div className="flex items-center mb-2">
                <Icon name="TrendingUp" size={16} className="mr-2 text-accent" />
                <span className="font-semibold text-accent">Business Impact</span>
              </div>
              <p className="text-text-primary">{phases?.[activePhase]?.businessImpact}</p>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-text-primary mb-4">Phase Activities:</h4>
            <ul className="space-y-3">
              {phases?.[activePhase]?.details?.map((detail, index) => (
                <li key={index} className="flex items-start">
                  <Icon name="Check" size={16} className="mr-3 mt-1 text-accent flex-shrink-0" />
                  <span className="text-text-secondary">{detail}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="mt-8 pt-6 border-t">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-text-secondary">
              Phase {activePhase + 1} of {phases?.length}
            </span>
            <span className="text-sm text-text-secondary">
              {Math.round(((activePhase + 1) / phases?.length) * 100)}% Complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-500"
              style={{ width: `${((activePhase + 1) / phases?.length) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversionProcess;